import { useRef, useEffect, useCallback, useState } from "react";
import type { VnProgram, Beat } from "../engine/Interpreter";

interface StoryGraphProps {
  program: VnProgram;
  currentScene: string;
}

interface GraphNode {
  id: string;
  x: number;
  y: number;
  vx: number;
  vy: number;
  isEntry: boolean;
  isDeadEnd: boolean;
  isUnreachable: boolean;
  isCurrent: boolean;
}

interface GraphEdge {
  from: string;
  to: string;
  isChoice: boolean;
}

function extractGraph(program: VnProgram, currentScene: string) {
  const nodes: Map<string, GraphNode> = new Map();
  const edges: GraphEdge[] = [];
  const incomingSet: Set<string> = new Set();
  const outgoingSet: Set<string> = new Set();

  for (const sceneId of Object.keys(program.scenes)) {
    nodes.set(sceneId, {
      id: sceneId,
      x: Math.random() * 600 + 50,
      y: Math.random() * 400 + 50,
      vx: 0,
      vy: 0,
      isEntry: sceneId === program.entry,
      isDeadEnd: false,
      isUnreachable: false,
      isCurrent: sceneId === currentScene,
    });
  }

  for (const [sceneId, scene] of Object.entries(program.scenes)) {
    collectEdges(sceneId, scene.beats, edges, outgoingSet, incomingSet);
  }

  // Dead-end: no outgoing edges (excluding self) and not the entry to END
  for (const [id, node] of nodes) {
    if (!outgoingSet.has(id)) {
      node.isDeadEnd = true;
    }
  }

  // Unreachable: not reachable from entry via BFS
  const reachable = new Set<string>();
  const queue = [program.entry];
  reachable.add(program.entry);
  while (queue.length > 0) {
    const current = queue.shift()!;
    for (const edge of edges) {
      if (edge.from === current && !reachable.has(edge.to)) {
        reachable.add(edge.to);
        queue.push(edge.to);
      }
    }
  }
  for (const [id, node] of nodes) {
    if (!reachable.has(id)) {
      node.isUnreachable = true;
    }
  }

  return { nodes: Array.from(nodes.values()), edges };
}

function collectEdges(
  sceneId: string,
  beats: Beat[],
  edges: GraphEdge[],
  outgoingSet: Set<string>,
  incomingSet: Set<string>,
) {
  for (const beat of beats) {
    if (beat.op === "jump" && beat.target?.scene) {
      const to = beat.target.scene;
      if (to !== sceneId) {
        edges.push({ from: sceneId, to, isChoice: false });
        outgoingSet.add(sceneId);
        incomingSet.add(to);
      }
    }
    if (beat.op === "choice" && beat.options) {
      for (const opt of beat.options) {
        if (opt.target?.scene && opt.target.scene !== sceneId) {
          edges.push({ from: sceneId, to: opt.target.scene, isChoice: true });
          outgoingSet.add(sceneId);
          incomingSet.add(opt.target.scene);
        }
        if (opt.branch) {
          collectEdges(sceneId, opt.branch, edges, outgoingSet, incomingSet);
        }
      }
    }
    if (beat.op === "if") {
      if (beat.then) collectEdges(sceneId, beat.then, edges, outgoingSet, incomingSet);
      if (beat.else) collectEdges(sceneId, beat.else, edges, outgoingSet, incomingSet);
    }
  }
}

const NODE_RADIUS = 20;
const REPULSION = 8000;
const ATTRACTION = 0.005;
const DAMPING = 0.85;
const IDEAL_EDGE_LEN = 150;
const CENTER_PULL = 0.01;

function simulate(nodes: GraphNode[], edges: GraphEdge[], width: number, height: number) {
  const cx = width / 2;
  const cy = height / 2;

  // Repulsion between all node pairs
  for (let i = 0; i < nodes.length; i++) {
    for (let j = i + 1; j < nodes.length; j++) {
      const dx = nodes[i].x - nodes[j].x;
      const dy = nodes[i].y - nodes[j].y;
      const dist = Math.max(Math.sqrt(dx * dx + dy * dy), 1);
      const force = REPULSION / (dist * dist);
      const fx = (dx / dist) * force;
      const fy = (dy / dist) * force;
      nodes[i].vx += fx;
      nodes[i].vy += fy;
      nodes[j].vx -= fx;
      nodes[j].vy -= fy;
    }
  }

  // Attraction along edges
  for (const edge of edges) {
    const from = nodes.find((n) => n.id === edge.from);
    const to = nodes.find((n) => n.id === edge.to);
    if (!from || !to) continue;
    const dx = to.x - from.x;
    const dy = to.y - from.y;
    const dist = Math.sqrt(dx * dx + dy * dy);
    const force = (dist - IDEAL_EDGE_LEN) * ATTRACTION;
    const fx = (dx / Math.max(dist, 1)) * force;
    const fy = (dy / Math.max(dist, 1)) * force;
    from.vx += fx;
    from.vy += fy;
    to.vx -= fx;
    to.vy -= fy;
  }

  // Center pull
  for (const node of nodes) {
    node.vx += (cx - node.x) * CENTER_PULL;
    node.vy += (cy - node.y) * CENTER_PULL;
  }

  // Apply velocity with damping
  for (const node of nodes) {
    node.vx *= DAMPING;
    node.vy *= DAMPING;
    node.x += node.vx;
    node.y += node.vy;
    // Constrain to bounds
    node.x = Math.max(NODE_RADIUS + 5, Math.min(width - NODE_RADIUS - 5, node.x));
    node.y = Math.max(NODE_RADIUS + 5, Math.min(height - NODE_RADIUS - 5, node.y));
  }
}

function drawGraph(
  ctx: CanvasRenderingContext2D,
  nodes: GraphNode[],
  edges: GraphEdge[],
  width: number,
  height: number,
) {
  ctx.clearRect(0, 0, width, height);

  // Draw edges
  for (const edge of edges) {
    const from = nodes.find((n) => n.id === edge.from);
    const to = nodes.find((n) => n.id === edge.to);
    if (!from || !to) continue;

    ctx.beginPath();
    ctx.moveTo(from.x, from.y);
    ctx.lineTo(to.x, to.y);
    ctx.strokeStyle = edge.isChoice ? "rgba(200, 160, 80, 0.6)" : "rgba(126, 200, 227, 0.4)";
    ctx.lineWidth = edge.isChoice ? 2 : 1.5;
    if (edge.isChoice) ctx.setLineDash([4, 4]);
    else ctx.setLineDash([]);
    ctx.stroke();
    ctx.setLineDash([]);

    // Arrow head
    const angle = Math.atan2(to.y - from.y, to.x - from.x);
    const arrowDist = NODE_RADIUS + 4;
    const ax = to.x - Math.cos(angle) * arrowDist;
    const ay = to.y - Math.sin(angle) * arrowDist;
    ctx.beginPath();
    ctx.moveTo(ax, ay);
    ctx.lineTo(ax - Math.cos(angle - 0.4) * 8, ay - Math.sin(angle - 0.4) * 8);
    ctx.lineTo(ax - Math.cos(angle + 0.4) * 8, ay - Math.sin(angle + 0.4) * 8);
    ctx.closePath();
    ctx.fillStyle = edge.isChoice ? "rgba(200, 160, 80, 0.8)" : "rgba(126, 200, 227, 0.6)";
    ctx.fill();
  }

  // Draw nodes
  for (const node of nodes) {
    ctx.beginPath();
    ctx.arc(node.x, node.y, NODE_RADIUS, 0, Math.PI * 2);

    if (node.isCurrent) {
      ctx.fillStyle = "rgba(80, 200, 120, 0.9)";
      ctx.strokeStyle = "#50c878";
    } else if (node.isEntry) {
      ctx.fillStyle = "rgba(126, 200, 227, 0.8)";
      ctx.strokeStyle = "#7ec8e3";
    } else if (node.isUnreachable) {
      ctx.fillStyle = "rgba(200, 60, 60, 0.6)";
      ctx.strokeStyle = "#c83c3c";
    } else if (node.isDeadEnd) {
      ctx.fillStyle = "rgba(200, 160, 60, 0.6)";
      ctx.strokeStyle = "#c8a03c";
    } else {
      ctx.fillStyle = "rgba(60, 60, 80, 0.8)";
      ctx.strokeStyle = "rgba(200, 200, 220, 0.4)";
    }

    ctx.lineWidth = node.isCurrent ? 3 : 1.5;
    ctx.fill();
    ctx.stroke();

    // Label
    ctx.fillStyle = "#fff";
    ctx.font = "10px monospace";
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    const label = node.id.length > 10 ? node.id.slice(0, 9) + "…" : node.id;
    ctx.fillText(label, node.x, node.y);
  }
}

export function StoryGraph({ program, currentScene }: StoryGraphProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animRef = useRef<number>(0);
  const nodesRef = useRef<GraphNode[]>([]);
  const edgesRef = useRef<GraphEdge[]>([]);
  const [stats, setStats] = useState({ scenes: 0, edges: 0, deadEnds: 0, unreachable: 0 });

  const width = 700;
  const height = 500;

  useEffect(() => {
    const { nodes, edges } = extractGraph(program, currentScene);
    nodesRef.current = nodes;
    edgesRef.current = edges;
    setStats({
      scenes: nodes.length,
      edges: edges.length,
      deadEnds: nodes.filter((n) => n.isDeadEnd).length,
      unreachable: nodes.filter((n) => n.isUnreachable).length,
    });
  }, [program, currentScene]);

  const draw = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    simulate(nodesRef.current, edgesRef.current, width, height);
    drawGraph(ctx, nodesRef.current, edgesRef.current, width, height);
    animRef.current = requestAnimationFrame(draw);
  }, [width, height]);

  useEffect(() => {
    animRef.current = requestAnimationFrame(draw);
    return () => cancelAnimationFrame(animRef.current);
  }, [draw]);

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
      <div style={legendStyle}>
        <span style={{ marginRight: "1rem" }}>Scenes: {stats.scenes}</span>
        <span style={{ marginRight: "1rem" }}>Edges: {stats.edges}</span>
        {stats.deadEnds > 0 && (
          <span style={{ color: "#c8a03c", marginRight: "1rem" }}>
            Dead-ends: {stats.deadEnds}
          </span>
        )}
        {stats.unreachable > 0 && (
          <span style={{ color: "#c83c3c" }}>
            Unreachable: {stats.unreachable}
          </span>
        )}
      </div>
      <div style={canvasContainerStyle}>
        <canvas
          ref={canvasRef}
          width={width}
          height={height}
          style={{ width: "100%", height: "100%", background: "rgba(5, 5, 15, 0.95)" }}
        />
      </div>
      <div style={legendStyle}>
        <span style={{ color: "#7ec8e3", marginRight: "0.75rem" }}>entry</span>
        <span style={{ color: "#50c878", marginRight: "0.75rem" }}>current</span>
        <span style={{ color: "#c8a03c", marginRight: "0.75rem" }}>dead-end</span>
        <span style={{ color: "#c83c3c", marginRight: "0.75rem" }}>unreachable</span>
        <span style={{ color: "rgba(200,160,80,0.8)" }}>--- choice</span>
      </div>
    </div>
  );
}

const legendStyle: React.CSSProperties = {
  display: "flex",
  flexWrap: "wrap",
  padding: "0.4rem 0.75rem",
  fontSize: "0.7rem",
  opacity: 0.8,
  gap: "0.25rem",
};

const canvasContainerStyle: React.CSSProperties = {
  flex: 1,
  overflow: "hidden",
  minHeight: 0,
};
